import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { receiveKeys } from './keys';

import type { TypeErrorResponseData } from '~/types/common.types';
import type {
  TypeGetReturnDisposalOperationTimesResponse,
  TypeReturnDisposalOperationTimeApi,
} from '~/types/receive.types';

/**
 * [입고관리 > 회송/폐기 관리] 회수예정일(입고가능일시) 조회 API
 * @note 선택 이슈·회수방법으로 서버가 날짜+시간 윈도우를 계산한다.
 */
const fetchReturnDisposalOperationTimes = async (
  inspectionIds: number[],
  rtsMethod: string
): Promise<TypeReturnDisposalOperationTimeApi[]> => {
  const { data } = await axiosInstance.get<TypeGetReturnDisposalOperationTimesResponse>(
    API_ENDPOINT.RECEIVE.GET_RETURN_DISPOSAL_OPERATION_TIMES,
    { params: { inspectionIds, rtsMethod }, paramsSerializer: { indexes: null } }
  );

  return data.data.operationTimes;
};

type TypeUseGetReturnDisposalOperationTimesQueryParams = {
  inspectionIds: number[];
  rtsMethod: string;
  enabled?: boolean;
};

/** [입고관리 > 회송/폐기 관리] 회수예정일(입고가능일시) 조회 Query Hook */
export const useGetReturnDisposalOperationTimesQuery = ({
  inspectionIds,
  rtsMethod,
  enabled = true,
}: TypeUseGetReturnDisposalOperationTimesQueryParams) => {
  return useQuery({
    queryKey: receiveKeys.returnDisposalOperationTimes(inspectionIds, rtsMethod),
    queryFn: () => fetchReturnDisposalOperationTimes(inspectionIds, rtsMethod),
    enabled: enabled && inspectionIds.length > 0,
    staleTime: 0,
    gcTime: 1000 * 30,
  });
};

/** [입고관리 > 회송/폐기 관리] 회수예정일 조회 Mock (날짜별 시간 윈도우 고정 응답) */
registerMockAPI<TypeGetReturnDisposalOperationTimesResponse | TypeErrorResponseData>({
  method: 'get',
  endpoint: API_ENDPOINT.RECEIVE.GET_RETURN_DISPOSAL_OPERATION_TIMES,
  label: '회송폐기관리/회수예정일 조회',
  scenarios: [
    {
      name: '정상 응답',
      response: {
        data: {
          operationTimes: [
            { capaId: 28619, date: '2026-07-03', startTime: '09:00', endTime: '12:00' },
            // 같은 날짜·동일 범위 다중 capaId (dedup: 첫 capaId만 옵션 노출)
            { capaId: 28620, date: '2026-07-03', startTime: '09:00', endTime: '12:00' },
            { capaId: 28795, date: '2026-07-03', startTime: '13:00', endTime: '18:00' },
            { capaId: 28999, date: '2026-07-04', startTime: '09:00', endTime: '18:00' },
            { capaId: 29143, date: '2026-07-07', startTime: '10:00', endTime: '17:00' },
            { capaId: 29144, date: '2026-07-10', startTime: '09:00', endTime: '13:00' },
          ],
        },
      },
    },
    {
      // 보류해제-회송 건: 서버가 컬리 지정 슬롯 1건만 반환하고 capaId 는 null (신청 시 capaId 미전송)
      name: '지정일 단일 (보류해제-회송)',
      response: {
        data: {
          operationTimes: [{ capaId: null, date: '2026-08-21', startTime: '09:00', endTime: '12:00' }],
        },
      },
    },
    {
      name: '가능 일시 없음',
      response: { data: { operationTimes: [] } },
    },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '회수예정일 정보를 불러오지 못했습니다.' } },
      status: 500,
    },
  ],
});
